import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Chip, Card, CardContent, Button, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { School as AcademyIcon, TrendingUp, AutoAwesome } from '@mui/icons-material';
import { useSecurityTier } from '../contexts/SecurityTierContext';
import { useAuth } from '../contexts/AuthContext';
import PortfolioChart from '../features/dashboard/widgets/PortfolioChart';
import ActiveStrategies from '../features/dashboard/widgets/ActiveStrategies';
import BudgetOverviewWidget from '../features/dashboard/widgets/BudgetOverview';
import ActivityFeed from '../features/dashboard/widgets/ActivityFeed';
import QuickActions from '../features/dashboard/widgets/QuickActions';
import { apiFetch } from '../utils/apiFetch';

const DashboardPage: React.FC = () => {
  const { tier, nextUpgradeAction } = useSecurityTier();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hasStrategies, setHasStrategies] = useState<boolean | null>(null);

  useEffect(() => {
    apiFetch('/paper/budget')
      .then(r => r.ok ? r.json() : null)
      .then(data => setHasStrategies(data?.strategies?.length > 0))
      .catch(() => setHasStrategies(false));
  }, []);

  return (
    <Box>
      {/* Top bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">Dein Paper-Trading Dashboard</Typography>
          <Typography variant="h5" fontWeight={700}>
            Willkommen{user?.full_name ? `, ${user.full_name}` : ' zurück'}
          </Typography>
        </Box>
        {nextUpgradeAction && (
          <Chip
            label={nextUpgradeAction === 'enable-2fa' ? 'Upgrade: 2FA aktivieren →' : 'Upgrade: API-Key einrichten →'}
            color="warning"
            variant="outlined"
            clickable
            onClick={() => navigate('/settings')}
          />
        )}
      </Box>

      {/* Welcome Card für neue User */}
      {hasStrategies === false && (
        <Card sx={{ mb: 3, borderRadius: 3, background: 'linear-gradient(135deg, rgba(25,118,210,0.1) 0%, rgba(156,39,176,0.1) 100%)' }}>
          <CardContent sx={{ py: 3 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Willkommen bei btrader!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Starte deine Trading-Reise — ganz ohne Risiko mit Paper Trading:
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<AutoAwesome />}
                onClick={() => navigate('/onboarding')}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Schnellstart-Assistent
              </Button>
              <Button
                variant="outlined"
                startIcon={<AcademyIcon />}
                onClick={() => navigate('/academy')}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Grundlagen lernen
              </Button>
              <Button
                variant="outlined"
                startIcon={<TrendingUp />}
                onClick={() => navigate('/strategies')}
                sx={{ textTransform: 'none', borderRadius: 2 }}
              >
                Strategie manuell erstellen
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Academy-Tipp */}
      {hasStrategies === true && (
        <Alert
          severity="info"
          icon={<AcademyIcon />}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/academy')}>
              Zur Academy
            </Button>
          }
          sx={{ mb: 2, borderRadius: 2 }}
        >
          Lerne die Grundlagen hinter deinen Strategien — RSI, MACD, Bollinger und mehr.
        </Alert>
      )}

      {/* Main grid */}
      <Grid container spacing={3}>
        {/* Left column — 2/3 width */}
        <Grid item xs={12} lg={8}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <PortfolioChart />
            <ActiveStrategies />
          </Box>
        </Grid>

        {/* Right column — 1/3 width */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <BudgetOverviewWidget />
            <ActivityFeed />
            <QuickActions />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage;
